import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * 스크롤 처리
 * - PUSH/REPLACE (새 화면 진입): 최상단으로 이동
 * - POP (뒤로/앞으로 가기): 떠나기 전 스크롤 위치로 복원
 *
 * 주의: 이 앱은 전역 CSS상 실제 스크롤 컨테이너가 window가 아니라 <body>다.
 *  - body 스크롤은 window의 'scroll' 이벤트로 오지 않으므로 capture 단계로 포착한다.
 *  - 저장/복원도 window.scrollY 가 아니라 실제 스크롤러의 scrollTop 으로 처리한다.
 * 복원 시 콘텐츠가 점진적으로 렌더되어 높이가 나중에 커지므로,
 *  ResizeObserver 로 높이 변화를 감시하다가 목표 Y에 도달 가능해지는 순간 복원한다.
 */
const scrollPositions = new Map();
const RESTORE_TIMEOUT_MS = 5000;

/** 실제로 스크롤되는 엘리먼트 선택 (이 앱은 body가 스크롤러) */
function getScroller() {
  const cands = [
    document.scrollingElement,
    document.body,
    document.documentElement,
  ].filter(Boolean);
  for (const el of cands) {
    if (el.scrollHeight - el.clientHeight > 1) return el;
  }
  return document.scrollingElement || document.documentElement || document.body;
}
function getScrollTop() {
  const s = getScroller();
  return s ? s.scrollTop : window.scrollY;
}
function getMaxScroll() {
  const s = getScroller();
  return s ? s.scrollHeight - s.clientHeight : 0;
}
function setScrollTop(y) {
  const s = getScroller();
  if (s) s.scrollTop = y;
  else window.scrollTo(0, y);
}
function scrollToTop() {
  // 어떤 엘리먼트가 스크롤러든 최상단으로
  window.scrollTo(0, 0);
  if (document.body) document.body.scrollTop = 0;
  if (document.documentElement) document.documentElement.scrollTop = 0;
}
/** 메인 스크롤러(body/html/document)의 스크롤만 추적 (내부 캐러셀 등은 무시) */
function isMainScrollTarget(t) {
  return (
    t === document ||
    t === document.body ||
    t === document.documentElement ||
    t === window
  );
}

export default function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType(); // "POP" | "PUSH" | "REPLACE"
  const currentKeyRef = useRef(location.key);
  const suppressRef = useRef(false);

  // 브라우저 기본 복원과 충돌 방지 (직접 제어)
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
  }, []);

  // 스크롤 위치 저장 — capture 단계로 body 스크롤까지 포착
  useEffect(() => {
    const onScroll = (e) => {
      if (suppressRef.current) return;
      if (!isMainScrollTarget(e.target)) return; // 내부 스크롤 영역은 제외
      scrollPositions.set(currentKeyRef.current, getScrollTop());
    };
    window.addEventListener("scroll", onScroll, true); // capture
    return () => window.removeEventListener("scroll", onScroll, true);
  }, []);

  // 위치 변경 시: POP이면 저장 위치 복원, 그 외엔 최상단
  useLayoutEffect(() => {
    currentKeyRef.current = location.key;
    suppressRef.current = true;

    const y =
      navType === "POP" && scrollPositions.has(location.key)
        ? scrollPositions.get(location.key)
        : 0;

    if (!(y > 0)) {
      scrollToTop();
      const raf = requestAnimationFrame(() => {
        suppressRef.current = false;
      });
      return () => cancelAnimationFrame(raf);
    }

    // ── 깊은 위치 복원 ──
    let done = false;
    let ro = null;
    let timer = 0;

    const onUserScroll = () => cleanup();
    function cleanup() {
      if (done) return;
      done = true;
      if (ro) ro.disconnect();
      if (timer) clearTimeout(timer);
      window.removeEventListener("wheel", onUserScroll);
      window.removeEventListener("touchstart", onUserScroll);
      window.removeEventListener("keydown", onUserScroll);
      suppressRef.current = false;
    }

    const tryRestore = () => {
      if (done) return;
      if (getMaxScroll() >= y - 2) {
        setScrollTop(y);
        cleanup();
      }
    };

    window.addEventListener("wheel", onUserScroll, { passive: true });
    window.addEventListener("touchstart", onUserScroll, { passive: true });
    window.addEventListener("keydown", onUserScroll);

    tryRestore(); // 즉시 시도 (콘텐츠가 이미 충분히 길면 바로 복원)
    if (!done && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => tryRestore());
      ro.observe(document.documentElement);
      ro.observe(document.body);
    }
    requestAnimationFrame(function frame() {
      if (done) return;
      tryRestore();
      requestAnimationFrame(() => !done && tryRestore());
    });
    timer = window.setTimeout(cleanup, RESTORE_TIMEOUT_MS);

    return cleanup;
  }, [location.key, navType]);

  return null;
}
